import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { t } from '../i18n';
import { useLocale } from '../i18n/useT';
import Wordmark from './ui/Wordmark';
import { CurrencyProvider, useFormatCurrency } from '../utils/currencyContext';
import { subscribeToRoom } from '../utils/roomManager';
import { isFoldedThisHand, isSeated, isWaitingNextHand, seatPresence } from '../utils/playerLedger';
import { seatStatusLabel } from '../utils/sitOutCopy';
import { potsForDisplay } from '../utils/potDisplay';
import { calculateSidePotsFromState } from '../utils/pokerLogic';
import { joinWinnerNames } from '../utils/winMessages';
import {
  betAnchor,
  computeTableMetrics,
  seatAngles,
  seatPosition,
} from '../utils/tableLayout';

// Read-only big-screen view. Subscribes to rooms/{code} and never writes —
// no presence, no join, no auth profile, no stats. Phone TableLayout is
// unchanged; this page only reuses tableLayout.js geometry.

const STAGE_REVEAL = { FLOP: 3, TURN: 4, RIVER: 5, SHOWDOWN: 5 };

function useMeasuredBox() {
  const ref = useRef(null);
  const [box, setBox] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const read = () => {
      const r = el.getBoundingClientRect();
      setBox((prev) =>
        Math.round(prev.width) === Math.round(r.width) && Math.round(prev.height) === Math.round(r.height)
          ? prev
          : { width: r.width, height: r.height }
      );
    };

    read();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', read);
      window.addEventListener('orientationchange', read);
      return () => {
        window.removeEventListener('resize', read);
        window.removeEventListener('orientationchange', read);
      };
    }

    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, box];
}

export function normalizeTvCode(raw) {
  return String(raw || '').trim().toUpperCase();
}

function seatedList(players) {
  return Object.values(players || {})
    .filter(isSeated)
    .sort((a, b) => (a.joinedAt || 0) - (b.joinedAt || 0));
}

function TvQuiet({ code, testId, children }) {
  return (
    <div className="h-[100dvh] bg-cream text-ink flex flex-col" data-testid={testId}>
      <div className="px-8 pt-6 flex-shrink-0">
        <Wordmark size="sm" />
      </div>
      <div className="flex-1 flex items-center justify-center px-8">
        <p className="text-[16px] text-mute m-0 text-center">{children}</p>
      </div>
      <TvFooter code={code} />
    </div>
  );
}

function TvFooter({ code }) {
  return (
    <div
      className="flex-shrink-0 px-8 pb-5 pt-2 text-center text-[12px] text-mute tracking-[0.04em]"
      data-testid="tv-footer"
    >
      playchipless.com · {code}
    </div>
  );
}

function turnLine(room, playersArray) {
  const game = room?.gameState;
  const winnerInfo = game?.winnerInfo;
  const winnerIds = Array.isArray(winnerInfo?.winnerIds) ? winnerInfo.winnerIds : [];
  if (winnerIds.length > 0) {
    const names = winnerIds.map((id) => playersArray.find((p) => p.id === id)?.name).filter(Boolean);
    if (names.length > 0) {
      return names.length > 1
        ? t('game.winTheHand', { names: joinWinnerNames(names) })
        : t('game.winsTheHand', { names: joinWinnerNames(names) });
    }
  }

  if (room?.status !== 'PLAYING') {
    return t('tv.waitingDeal');
  }

  const turnId = game?.currentTurnPlayerId;
  const turnPlayer = playersArray.find((p) => p.id === turnId);
  if (turnPlayer && !isWaitingNextHand(turnPlayer)) {
    return t('game.turnOf', { name: turnPlayer.name });
  }
  if (game?.canAdvanceStage) {
    return t('tv.waitingHost');
  }
  if (game?.stage === 'SHOWDOWN') {
    return t('game.stage.showdown');
  }
  return t('tv.waitingHost');
}

function TvSeat({ player, angle, tableW, tableH, seat, isCurrentTurn, isWinner }) {
  const formatCurrency = useFormatCurrency();
  const { x, y } = seatPosition(angle, tableW, tableH);
  const presence = seatPresence(player);
  const waitingLook = presence === 'away' || presence === 'next';
  const out = presence === 'out';
  const dark = !waitingLook && !out && (isCurrentTurn || isWinner);
  const { micro } = seat;
  const dense = seat.dense || micro;
  const folded = !waitingLook && !out && isFoldedThisHand(player);
  const hatch = folded || out;

  const isDealer = player.role === 'DEALER' || player.role === 'DEALER_SMALL_BLIND';
  const isSmallBlind = player.role === 'SMALL_BLIND' || player.role === 'DEALER_SMALL_BLIND';
  const isBigBlind = player.role === 'BIG_BLIND';
  const roleText = out
    ? null
    : (presence === 'pending' || presence === 'away' || presence === 'next')
    ? seatStatusLabel(presence, { dense: true, micro })
    : isDealer && isSmallBlind ? t('poker.dSb') : isDealer ? t('poker.d') : isSmallBlind ? t('poker.sb') : isBigBlind ? t('poker.bb') : null;

  const badgeCls = micro
    ? `text-[10px] font-semibold tracking-[0.07em] flex-shrink-0 ${dark ? 'text-white/70' : 'text-mute'}`
    : `${dense ? 'text-[10px]' : 'text-[11px]'} font-semibold tracking-[0.07em] px-[5px] py-[1px] rounded-[4px] border flex-shrink-0 ${
        dark ? 'border-white/30 text-white/85' : 'border-line text-mute'
      }`;

  const tone = dark
    ? 'bg-ink text-cream border-ink'
    : waitingLook
    ? 'border-dashed border-line bg-cream-2 text-mute'
    : hatch
    ? 'border-dashed border-line-2 text-mute cl-folded-hatch bg-cream'
    : 'bg-cream text-ink border-line';

  const bet = player.currentRoundBet || 0;
  const nameCls = micro
    ? 'text-[13px]'
    : dense ? 'text-[14px]' : 'text-[15px]';
  const stackCls = micro
    ? 'text-[11px]'
    : dense ? 'text-[12px]' : 'text-[13px]';

  return (
    <div
      data-seat-id={player.id}
      data-seat-offset={y}
      data-testid={`seat-${player.name}`}
      className="absolute -translate-x-1/2 -translate-y-1/2 z-[4]"
      style={{ left: `calc(50% + ${x}px)`, top: `calc(50% + ${y}px)` }}
    >
      <div
        className={`relative border ${
          micro ? 'rounded-[10px] px-1.5 py-1' : dense ? 'rounded-[11px] px-2.5 py-1.5' : 'rounded-[12px] px-3 py-[7px]'
        } ${tone} ${
          isCurrentTurn && !waitingLook ? 'shadow-[0_0_0_3px_rgba(13,13,13,0.11)]' : ''
        }`}
        style={{ width: `${seat.width}px` }}
      >
        <div className="flex items-center gap-1 min-w-0">
          <span
            data-testid={`seat-name-${player.name}`}
            className={`${nameCls} tracking-[-0.01em] truncate leading-tight min-w-0 ${
              folded ? 'font-medium text-mute line-through decoration-mute/70' : (waitingLook || out) ? 'font-medium text-mute' : 'font-semibold'
            }`}
          >
            {player.name}
          </span>
          {roleText && (
            <span className={badgeCls} data-testid={`seat-role-${player.name}`}>
              {roleText}
            </span>
          )}
        </div>
        <div className={`${stackCls} tnum leading-tight mt-0.5 truncate ${dark ? 'opacity-75' : 'text-mute'}`}>
          {player.isAllIn && player.stack === 0 ? (
            <span className="tracking-[0.06em] font-semibold">{t('poker.allIn')}</span>
          ) : (
            <>
              <span data-testid={`stack-${player.name}`}>{formatCurrency(player.stack)}</span>
              {player.isAllIn && (
                <span className={`ml-1 tracking-[0.06em] font-semibold ${dark ? 'opacity-70' : ''}`}>{t('poker.allIn')}</span>
              )}
            </>
          )}
        </div>
      </div>

      {bet > 0 && (
        <div
          data-testid={`bet-${player.name}`}
          className={`absolute left-1/2 ${stackCls} font-semibold tnum max-w-[96px] truncate bg-cream border border-line rounded-full px-[9px] py-[3px] z-[3]`}
          style={
            betAnchor(angle) === 'below'
              ? { top: '100%', transform: 'translate(-50%, 10px)' }
              : { bottom: '100%', transform: 'translate(-50%, -10px)' }
          }
        >
          {formatCurrency(bet)}
        </div>
      )}
    </div>
  );
}

function TvTable({ room, code }) {
  const formatCurrency = useFormatCurrency();
  const [wrapRef, box] = useMeasuredBox();
  const players = room?.players;
  const playersArray = useMemo(() => seatedList(players), [players]);
  const game = room?.gameState;
  const currentStage = game?.stage || 'PRE_FLOP';
  const handNumber = game?.handNumber || 1;
  const settings = room?.settings || {};

  const allPlayers = useMemo(
    () => Object.values(players || {}).sort((a, b) => (a.joinedAt || 0) - (b.joinedAt || 0)),
    [players],
  );
  const totalPot = allPlayers.reduce((sum, p) => sum + (p.totalPotContribution || 0), 0);
  const rawPots = game?.pots && game.pots.length > 0
    ? game.pots
    : calculateSidePotsFromState({ players: allPlayers });
  const currentPots = potsForDisplay(rawPots, allPlayers);
  const multiPot = (currentPots?.length ?? 0) > 1;
  const winnerIds = Array.isArray(game?.winnerInfo?.winnerIds) ? game.winnerInfo.winnerIds : [];

  const metrics = useMemo(
    () => computeTableMetrics(box.width, box.height, playersArray.length, 'tv'),
    [box.width, box.height, playersArray.length],
  );
  const angles = useMemo(
    () => seatAngles(playersArray.length, 0),
    [playersArray.length],
  );

  const { tableW, tableH, scale, seat, overflowing } = metrics;
  const revealed = STAGE_REVEAL[currentStage] || 0;
  const px = (n) => `${n}px`;
  const sb = settings.smallBlind ?? 100;
  const bb = settings.bigBlind ?? 200;
  const line = turnLine(room, playersArray);

  return (
    <div className="h-[100dvh] bg-cream text-ink flex flex-col overflow-hidden" data-testid="tv-screen">
      <div className="px-8 pt-5 flex-shrink-0">
        <div className="flex items-center justify-between">
          <Wordmark size="sm" />
          <div
            className="text-[11px] text-mute tracking-[0.09em] uppercase tnum"
            data-testid="tv-hand-meta"
          >
            {room?.status === 'PLAYING' ? (
              <>
                <span data-testid="hand-number">{t('game.handNumber', { n: handNumber })}</span>
                <span className="mx-1.5">·</span>
                <span>{t('poker.blinds')} {formatCurrency(sb)}/{formatCurrency(bb)}</span>
              </>
            ) : (
              <span>{t('tv.lobby')}</span>
            )}
          </div>
        </div>
      </div>

      <div
        ref={wrapRef}
        dir="ltr"
        className={`i18n-ltr flex-1 min-h-0 relative mx-8 mt-2 ${overflowing ? 'overflow-y-auto' : 'overflow-hidden'}`}
      >
        <div
          aria-hidden="true"
          data-testid="table-oval"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-cream-2 border border-line"
          style={{ width: px(tableW), height: px(tableH) }}
        >
          <span className="absolute inset-[12px] rounded-[50%] border border-line-2 bg-cream" />
        </div>

        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center z-[2]"
          style={{ width: px(Math.round(200 * scale)) }}
        >
          <div data-testid="board-cards" className="flex justify-center" style={{ gap: px(Math.round(8 * scale)), marginBottom: px(Math.round(14 * scale)) }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{ width: px(Math.round(24 * scale)), height: px(Math.round(32 * scale)) }}
                className={`rounded-[4px] border ${i < revealed ? 'bg-ink border-ink' : 'bg-transparent border-line'}`}
              />
            ))}
          </div>

          <div className="flex items-center justify-center gap-1.5" style={{ height: px(Math.round(16 * scale)), marginBottom: px(Math.round(6 * scale)) }}>
            <span className="label-eyebrow" style={{ fontSize: px((11 * scale).toFixed(1)) }}>{t('poker.pot')}</span>
            {multiPot && (
              <span className="text-[10px] font-semibold tracking-[0.07em] px-[6px] py-[2px] rounded-[4px] border border-line text-mute bg-cream">
                {currentPots.length} {t('poker.potsCaps')}
              </span>
            )}
          </div>

          <div
            className="font-medium tracking-[-0.035em] tnum leading-none"
            style={{ fontSize: px(Math.round(44 * scale)) }}
            data-testid="pot-total"
          >
            {formatCurrency(totalPot)}
          </div>
        </div>

        {playersArray.map((player, i) => (
          <TvSeat
            key={player.id}
            player={player}
            angle={angles[i]}
            tableW={tableW}
            tableH={tableH}
            seat={seat}
            isCurrentTurn={game?.currentTurnPlayerId === player.id && seatPresence(player) !== 'away' && seatPresence(player) !== 'next'}
            isWinner={winnerIds.includes(player.id) && !player.hasFolded && seatPresence(player) !== 'away' && seatPresence(player) !== 'next'}
          />
        ))}
      </div>

      <div className="flex-shrink-0 px-8 pt-3 text-center">
        <p
          className="text-[22px] font-medium tracking-[-0.02em] text-ink m-0"
          data-testid="tv-turn"
        >
          {line}
        </p>
      </div>

      <TvFooter code={code} />
    </div>
  );
}

function TvScreenBody({ code, room }) {
  if (!code || room === null) {
    return (
      <TvQuiet code={code || '————'} testId="tv-missing">
        {t('tv.missing')}
      </TvQuiet>
    );
  }

  if (room === undefined) {
    return (
      <TvQuiet code={code} testId="tv-loading">
        {t('tv.loading')}
      </TvQuiet>
    );
  }

  if (room.status === 'ENDED') {
    return (
      <TvQuiet code={code} testId="tv-ended">
        {t('tv.ended')}
      </TvQuiet>
    );
  }

  return <TvTable room={room} code={code} />;
}

export default function TvScreen() {
  useLocale();
  const { code: raw } = useParams();
  const code = normalizeTvCode(raw);
  const [room, setRoom] = useState(undefined);

  useEffect(() => {
    if (!code) {
      setRoom(null);
      return undefined;
    }
    return subscribeToRoom(code, setRoom);
  }, [code]);

  return (
    <>
      <Helmet>
        <title>{code ? `Chipless · ${code}` : 'Chipless'}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <CurrencyProvider currency={room?.settings?.currency}>
        <TvScreenBody code={code} room={room} />
      </CurrencyProvider>
    </>
  );
}
